import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { fmtPeriod, type CbgCountry, type CbgDTO } from "@/lib/central-bank-gold";

type SortKey = "tonnes" | "m12" | "m1" | "ytd" | "goldShare";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "tonnes", label: "Holdings" },
  { key: "m12", label: "12M change" },
  { key: "ytd", label: "YTD change" },
  { key: "m1", label: "1M change" },
  { key: "goldShare", label: "Gold share" },
];

const fmtT = (x: number) => x.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function Change({ v }: { v: number | null }) {
  if (v == null) return <span className="text-muted-foreground">n/a</span>;
  if (Math.abs(v) < 0.05) return <span className="text-muted-foreground">0.0</span>;
  return <span className={v > 0 ? "text-emerald" : "text-destructive"}>{v > 0 ? "+" : ""}{fmtT(v)}</span>;
}

function Sparkline({ series }: { series: [string, number][] }) {
  if (series.length < 2) return null;
  const vals = series.map((s) => s[1]);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const w = 600;
  const h = 120;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - 8 - ((v - min) / span) * (h - 16)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-28 w-full" preserveAspectRatio="none" role="img" aria-label="Monthly gold holdings">
      <polyline points={pts} fill="none" stroke="var(--gold)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Bars({ title, rows, tone }: { title: string; rows: CbgCountry[]; tone: "buy" | "sell" }) {
  const max = Math.max(...rows.map((r) => Math.abs(r.m12 ?? 0)), 1);
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{title}</div>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No reporter in this group.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((r) => (
            <li key={r.code}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-foreground">{r.name}</span>
                <span className={`font-mono ${tone === "buy" ? "text-emerald" : "text-destructive"}`}>
                  {tone === "buy" ? "+" : ""}{fmtT(r.m12 ?? 0)} t
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-muted/40">
                <div
                  className={`h-1.5 rounded-full ${tone === "buy" ? "bg-emerald" : "bg-destructive"}`}
                  style={{ width: `${(Math.abs(r.m12 ?? 0) / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CentralBankGoldTracker({ data }: { data: CbgDTO }) {
  const [sort, setSort] = useState<SortKey>("tonnes");
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<string>("CHN");

  const fresh = data.countries.filter((c) => !c.stale);
  const buyers = [...fresh].filter((c) => (c.m12 ?? 0) >= 0.5).sort((a, b) => (b.m12 ?? 0) - (a.m12 ?? 0)).slice(0, 8);
  const sellers = [...fresh].filter((c) => (c.m12 ?? 0) <= -0.5).sort((a, b) => (a.m12 ?? 0) - (b.m12 ?? 0)).slice(0, 5);
  const ranks = useMemo(() => new Map(data.countries.map((c, i) => [c.code, i + 1])), [data.countries]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = data.countries.filter((c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
    const val = (c: CbgCountry) => (c[sort] ?? Number.NEGATIVE_INFINITY) as number;
    return [...list].sort((a, b) => val(b) - val(a));
  }, [data.countries, query, sort]);
  const visible = showAll || query ? rows : rows.slice(0, 25);
  const detail = data.countries.find((c) => c.code === selected) ?? data.countries[0];

  const kpis = [
    { label: "Reported official gold", value: `${fmtT(data.totals.tonnes)} t`, note: `${data.totals.reporting} reporting institutions` },
    {
      label: "12 month net change",
      value: `${data.totals.m12Net > 0 ? "+" : ""}${fmtT(data.totals.m12Net)} t`,
      note: "Sum across current reporters",
      tone: data.totals.m12Net > 0 ? "text-emerald" : data.totals.m12Net < 0 ? "text-destructive" : "",
    },
    { label: "Buyers vs sellers", value: `${data.totals.buyers12} / ${data.totals.sellers12}`, note: "Net change of 0.5 t or more over 12 months" },
    {
      label: "Latest data month",
      value: fmtPeriod(data.latestPeriod),
      note: data.marketPrice ? `Month end gold near $${Math.round(data.marketPrice).toLocaleString("en-US")}/oz` : "IMF IRFCL",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-border/60 bg-card/60 p-4 sm:p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{k.label}</div>
            <div className={`mt-2 font-display text-xl font-semibold sm:text-2xl ${k.tone ?? "text-foreground"}`}>{k.value}</div>
            <div className="mt-1 text-xs text-muted-foreground">{k.note}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Bars title="Largest buyers, last 12 months" rows={buyers} tone="buy" />
        <Bars title="Largest sellers, last 12 months" rows={sellers} tone="sell" />
      </div>

      <div className="rounded-xl border border-border/60 bg-card/60 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Holdings history</div>
            <div className="mt-1 font-display text-xl font-semibold">{detail.name}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-xl font-semibold">{fmtT(detail.tonnes)} t</div>
            <div className="text-xs text-muted-foreground">As of {fmtPeriod(detail.asOf)}</div>
          </div>
        </div>
        <div className="mt-4">
          <Sparkline series={detail.series} />
          <div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">
            <span>{detail.series[0] ? fmtPeriod(detail.series[0][0]) : ""}</span>
            <span>{detail.series.at(-1) ? fmtPeriod(detail.series.at(-1)![0]) : ""}</span>
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-muted-foreground">1 month</dt><dd className="font-mono"><Change v={detail.m1} /> t</dd></div>
          <div><dt className="text-xs text-muted-foreground">Year to date</dt><dd className="font-mono"><Change v={detail.ytd} /> t</dd></div>
          <div><dt className="text-xs text-muted-foreground">12 months</dt><dd className="font-mono"><Change v={detail.m12} /> t</dd></div>
          <div>
            <dt className="text-xs text-muted-foreground">Gold share of reserves</dt>
            <dd className="font-mono">{detail.goldShare == null ? "n/a" : `${detail.goldShare.toFixed(1)}%`}</dd>
          </div>
        </dl>
        {detail.valuation === "book" && (
          <p className="mt-3 text-xs text-muted-foreground">
            This central bank carries gold at a statutory or historical cost price. Volume is shown as reported and the gold share is
            restated at the month end market price.
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">Select any row in the table below to load its history.</p>
      </div>

      <div className="rounded-xl border border-border/60 bg-card/60">
        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
          <label className="relative min-w-[200px] flex-1">
            <span className="sr-only">Search country</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country"
              className="h-9 w-full rounded-md border border-border/60 bg-background/60 pl-9 pr-3 text-sm outline-none focus:border-gold/60"
            />
          </label>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Sort table">
            {SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSort(s.key)}
                aria-pressed={sort === s.key}
                className={`rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  sort === s.key ? "border-gold/60 bg-gold-muted text-gold" : "border-border/60 text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <caption className="sr-only">Central bank gold reserves by country, in tonnes</caption>
            <thead className="bg-muted/30 text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">#</th>
                <th className="p-3">Country</th>
                <th className="p-3 text-right">Tonnes</th>
                <th className="p-3 text-right">1M</th>
                <th className="p-3 text-right">YTD</th>
                <th className="p-3 text-right">12M</th>
                <th className="p-3 text-right">Gold share</th>
                <th className="p-3 text-right">As of</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {visible.map((c) => (
                <tr
                  key={c.code}
                  onClick={() => setSelected(c.code)}
                  className={`cursor-pointer transition-colors hover:bg-muted/20 ${selected === c.code ? "bg-gold-muted" : ""}`}
                >
                  <td className="p-3 font-mono text-muted-foreground">{ranks.get(c.code)}</td>
                  <td className="p-3">
                    <button type="button" onClick={() => setSelected(c.code)} className="text-left font-medium text-foreground hover:text-gold">
                      {c.name}
                    </button>
                    {c.valuation === "book" && <span className="ml-2 font-mono text-[9px] uppercase text-muted-foreground">book value</span>}
                  </td>
                  <td className="p-3 text-right font-mono">{fmtT(c.tonnes)}</td>
                  <td className="p-3 text-right font-mono"><Change v={c.m1} /></td>
                  <td className="p-3 text-right font-mono"><Change v={c.ytd} /></td>
                  <td className="p-3 text-right font-mono"><Change v={c.m12} /></td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{c.goldShare == null ? "n/a" : `${c.goldShare.toFixed(1)}%`}</td>
                  <td className={`p-3 text-right font-mono ${c.stale ? "text-gold" : "text-muted-foreground"}`}>{fmtPeriod(c.asOf)}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No reporter matches this search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!query && rows.length > 25 && (
          <div className="border-t border-border/60 p-3 text-center">
            <button type="button" onClick={() => setShowAll((v) => !v)} className="text-sm text-gold hover:underline">
              {showAll ? "Show top 25" : `Show all ${rows.length} reporters`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
